import React, { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';

/**
 * Emergent-managed Google sign-in callback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 *
 * Flow:
 *   1. User taps "Continue with Google" → we send them to auth.emergentagent.com
 *      with redirect back to `${window.location.origin}/dashboard`.
 *   2. On return, the URL contains `#session_id=...` in the fragment.
 *   3. This component reads that fragment via useLocation().hash (reactive),
 *      exchanges it server-side for a persistent `session_token` cookie, then
 *      calls `login(user)` and navigates to /dashboard.
 */
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    const clearHash = () => {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch (_e) { /* ignore */ }
    };

    if (!sessionId) {
      clearHash();
      navigate('/auth', { replace: true });
      return;
    }

    (async () => {
      try {
        const resp = await axios.post(
          `${API}/auth/oauth/session`,
          null,
          { headers: { 'X-Session-ID': sessionId } }
        );
        clearHash();
        const user = resp?.data?.user;
        if (user && login) login(user, false, '');
        navigate('/dashboard', { replace: true, state: { user } });
      } catch (e) {
        clearHash();
        toast.error(e.response?.data?.detail || 'Google sign-in failed');
        navigate('/auth', { replace: true });
      }
    })();
  }, []);// eslint runs once — guarded by processed ref

  return (
    <div className="flex items-center justify-center min-h-screen" data-testid="auth-callback">
      <div className="text-center space-y-3">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-cyan-200 border-t-cyan-600 mx-auto"></div>
        <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
